import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

// Theme modes: standard (light/dark/system) or diary theme
export type Theme = 'light' | 'dark' | 'system' | 'diary' | 'diary-dark'

// Visual style: 'modern' (current) or 'diary' (Dear Diary theme)
export type ThemeStyle = 'modern' | 'diary'

interface ThemeState {
  theme: Theme
  isDark: boolean
  style: ThemeStyle // Track which visual style is active
}

const initialState: ThemeState = {
  theme: 'system',
  isDark: false,
  style: 'modern',
}

export const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.theme = action.payload
      
      // Update isDark and style based on theme
      if (action.payload === 'dark') {
        state.isDark = true
        state.style = 'modern'
      } else if (action.payload === 'light') {
        state.isDark = false
        state.style = 'modern'
      } else if (action.payload === 'diary') {
        state.isDark = false
        state.style = 'diary'
      } else if (action.payload === 'diary-dark') {
        state.isDark = true
        state.style = 'diary'
      } else {
        // System theme
        state.isDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        state.style = 'modern'
      }
    },
    
    toggleTheme: (state) => {
      // Toggle between light and dark within the current style
      if (state.style === 'diary') {
        if (state.theme === 'diary') {
          state.theme = 'diary-dark'
          state.isDark = true
        } else {
          state.theme = 'diary'
          state.isDark = false
        }
      } else {
        if (state.theme === 'light' || (state.theme === 'system' && !state.isDark)) {
          state.theme = 'dark'
          state.isDark = true
        } else {
          state.theme = 'light'
          state.isDark = false
        }
      }
    },
    
    setSystemTheme: (state, action: PayloadAction<boolean>) => {
      if (state.theme === 'system') {
        state.isDark = action.payload
      }
    },
    
    // Switch between modern and diary styles
    setThemeStyle: (state, action: PayloadAction<ThemeStyle>) => {
      state.style = action.payload
      
      if (action.payload === 'diary') {
        // Switch to diary theme, preserving dark preference
        state.theme = state.isDark ? 'diary-dark' : 'diary'
      } else {
        // Switch to modern theme, preserving dark preference
        state.theme = state.isDark ? 'dark' : 'light'
      }
    },
    
    // Toggle between modern and diary styles
    toggleThemeStyle: (state) => {
      if (state.style === 'modern') {
        state.style = 'diary'
        state.theme = state.isDark ? 'diary-dark' : 'diary'
      } else {
        state.style = 'modern'
        state.theme = state.isDark ? 'dark' : 'light'
      }
    },
  },
})

export const { 
  setTheme, 
  toggleTheme, 
  setSystemTheme,
  setThemeStyle,
  toggleThemeStyle,
} = themeSlice.actions

// Selector helpers
export const selectIsDiaryTheme = (state: { theme: ThemeState }) => 
  state.theme.style === 'diary'

export const selectThemeStyle = (state: { theme: ThemeState }) => 
  state.theme.style